import React, { useState } from 'react';

import queryString from 'query-string';
import { Link, useLocation } from 'react-router-dom';

import { IconButton } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

import { AddJobFlow, JOB_FLOW_DESCRIPTION_STEP } from '../organization/add-job/add-job-flow';
import { ProductTelemetryController } from '../product-telemetry/product-telemetry-controller';
import * as WebRoutes from '../web-routes';

import { MenuTabOptions } from './ui-icon-nav';

import { AiAutoFixIcon, MaterialDesignIcon } from '../util/material-design-icons';
import { trackWithSmartlook } from '../util/track-with-smartlook';

type InputProps = {
  orgId?: number;
  onClick?: React.MouseEventHandler<any>;
  iconName?: string;
  label: string;
  className?: string;
  whitelistedQueryParams?: string[];
  previousLocationPath?: string;
} & MenuTabOptions;

type Props = InputProps & React.PropsWithChildren<{}>;

const parseQueryParams = (qp: string, allowedValues: string[]) => {
  const allParams = queryString.parse(qp);

  return allowedValues.reduce((acc: Record<string, any>, param: string) => {
    if (allParams[param]) {
      acc[param] = allParams[param];
    }
    return acc;
  }, {});
};

export const UiNavIconTab = ({
  orgId,
  route,
  onClick,
  iconName,
  label,
  className,
  customAttribute,
  children,
  whitelistedQueryParams,
  previousLocationPath,
}: Props) => {
  const { pathname, search } = useLocation();

  // IF (05/24/21): Because we preface our routes with /org/:orgId/employees/something we want to
  // make sure we're only highlighting the dashboard and directory if it's an exact match and the
  // other routes can include params so just check for a partial match
  const tabIsSelected = () => {
    if (!route) {
      return false;
    }
    if (route === WebRoutes.HOME_ROUTE) {
      // we don't have a nav bar on our '/' route which is only visible to TE
      // admins and account group users. It will always match with the default
      // case of pathname.includes so we need a special case here to make sure
      // it won't appear selected
      return false;
    }
    if (
      orgId &&
      [
        WebRoutes.orgRoute(orgId),
        WebRoutes.employeesRoute(orgId),
        WebRoutes.orgJobsRoute(orgId),
      ].includes(route)
    ) {
      return pathname === route;
    }
    if (orgId && WebRoutes.orgInProgressRoute(orgId) === route) {
      return [WebRoutes.orgInProgressRoute(orgId), WebRoutes.orgJobsRoute(orgId)].includes(
        pathname,
      );
    }
    return pathname.includes(route);
  };

  // For generatedJobDescriptions
  const isJobDetailsTab = label === 'Job Details';
  const [isEditing, setIsEditing] = useState(false);

  let jobId: number | null = null;
  const matches = pathname.match(/org\/(\d+)\/jobs\/(\d+)/);
  if (isJobDetailsTab && matches && matches.length > 1 && (!orgId || !jobId)) {
    if (!orgId) {
      orgId = Number(matches[1]);
    }
    if (!jobId) {
      jobId = Number(matches[2]);
    }
  }

  const smartlookEvent = {
    eventName: 'Suggest wand click',
    props: {
      variant: 'icon',
      interaction: 'open',
      orgId: orgId ?? '',
      jobId: jobId ?? '',
    },
  };

  const handleEditClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    trackWithSmartlook(smartlookEvent);

    setIsEditing(true);
  };
  const handleClose = () => {
    trackWithSmartlook({
      ...smartlookEvent,
      props: {
        ...smartlookEvent.props,
        interaction: 'close',
      },
    });
    setIsEditing(false);
  };
  const handleLinkClick = (event: React.MouseEvent<any, MouseEvent>) => {
    ProductTelemetryController.logEvent('clicked navigation tab', {
      value: label,
    });
    if (onClick) {
      onClick(event);
    }
  };

  const suggestDisabled = !orgId || !jobId;

  const content = (
    <>
      {iconName ? (
        <MaterialDesignIcon
          iconName={iconName}
          color="gray"
          className={`nav-icon ${tabIsSelected() ? 'nav-icon--selected' : ''}`}
        />
      ) : null}
      {isJobDetailsTab ? (
        <div className="u-margin-left-2x typo-title u-flexGrow-1 u-spread">
          {label}
          <Tooltip title="Suggest job description improvements">
            <span>
              <IconButton
                className="u-margin-left-2x u-backgroundHover-transparent"
                size="small"
                onClick={handleEditClick}
                disabled={suggestDisabled}
              >
                <AiAutoFixIcon className="u-color-darkPurple" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      ) : (
        <div className="u-margin-left-2x typo-title">{label}</div>
      )}
      {children}
    </>
  );

  const navTabBaseClasses = `leftNav-tab ${tabIsSelected() ? 'leftNav-tab--selected' : ''}`;
  const navTabClasses = `${navTabBaseClasses} ${className || ''}`;

  const filteredParams = whitelistedQueryParams
    ? parseQueryParams(search, whitelistedQueryParams)
    : {};

  const queryParams = queryString.stringify(filteredParams);
  const fullRoute = queryParams && route ? `${route}?${queryParams}` : route;

  return (
    <>
      {fullRoute ? (
        <Link
          to={{ pathname: fullRoute, state: { previousLocationPath } }}
          onClick={handleLinkClick}
          className={navTabClasses}
        >
          {content}
        </Link>
      ) : (
        <div
          role="presentation"
          className={`u-cursor-pointer ${navTabClasses}`}
          onClick={onClick}
          {...customAttribute}
        >
          {content}
        </div>
      )}
      {isEditing && orgId && jobId && (
        <AddJobFlow
          initialStep={JOB_FLOW_DESCRIPTION_STEP}
          jobId={jobId}
          orgId={orgId}
          editing={isEditing}
          onClose={handleClose}
          onFinished={handleClose}
        />
      )}
    </>
  );
};
